import type React from "react";
import { twMerge } from "tailwind-merge";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	name: string;
	label: string | React.ReactNode;
	className?: string;
	styleType?: "primary" | "primary-alt" | "secondary" | "combined" | "link";
	type?: "button" | "submit" | "reset";
	disabled?: boolean;
	invalid?: boolean;
	valid?: boolean;
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	theme?: Theme;
}

export const Button = ({
	name,
	label,
	className = "",
	styleType = "primary",
	type = "submit",
	disabled = false,
	invalid = false,
	valid = false,
	theme = "light",
	...props
}: ButtonProps) => {
	let buttonStyles = ["py-3 px-5 w-full"];
	switch (styleType) {
		case "primary":
			buttonStyles.push(
				`${
					disabled
						? "bg-secondary-200 border-secondary-200 cursor-not-allowed"
						: "bg-secondary-900 border-secondary-900 hover:bg-secondary-800"
				} border text-white rounded-full font-semibold`,
			);
			break;
		case "primary-alt":
			buttonStyles.push(
				`${
					disabled
						? "bg-primary-200 border-primary-200 cursor-not-allowed"
						: "bg-primary border-primary hover:bg-primary-600"
				} border text-white rounded-full font-semibold`,
			);
			break;
		case "secondary":
			buttonStyles.push(
				`${
					disabled
						? "text-secondary-200 cursor-not-allowed"
						: "text-secondary-900 hover:bg-secondary-50"
				} border border-secondary-200 rounded-full font-semibold`,
			);
			break;
		case "combined":
			buttonStyles.push(
				`${
					disabled
						? "text-secondary-200 cursor-not-allowed"
						: "text-secondary-900 hover:bg-secondary-50"
				} border ${
					invalid
						? `border-primary ${
								theme === "light"
									? "border-l-secondary-200"
									: "border-l-secondary-700"
							}`
						: valid
							? "border-positive-500 border-l-secondary-200"
							: theme === "light"
								? "border-secondary-200"
								: "border-secondary-700"
				} rounded-r font-normal`,
			);
			break;
		case "link":
			buttonStyles = [
				"text-primary-600 hover:text-primary-700 font-semibold whitespace-nowrap",
			];
	}

	return (
		<button
			className={twMerge(buttonStyles.join(" "), className)}
			name={name}
			type={type}
			aria-label={name}
			disabled={disabled}
			{...props}
		>
			{label}
		</button>
	);
};
